import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import card_services from "services/card_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { apiErrorMessage } from "utils/errors/parseApiError";

export interface UseBlockCardReturn {
  blockingCard: boolean;
  blockingCardMsg: string;
  blockCardError: boolean;
  blockCardErrorMsg: string;
  blockCardSuccess: boolean;
  blockCardSuccessMsg: string;
  handleBlockCard: (cardNumber: string) => Promise<void>;
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
}

export function useBlockCard(): UseBlockCardReturn {
  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [lastCardNumber, setLastCardNumber] = useState("");

  const openError = (msg: string) => {
    setLoadingMsg("");
    setIsLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMsg("");
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const handleBlockCard = async (cardNumber: string) => {
    setLastCardNumber(cardNumber);

    try {
      setLoadingMsg("Blocking card…");
      setIsLoading(true);

      const result = await card_services.blockCard(cardNumber, networkType);
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      queryClient.invalidateQueries({ queryKey: ["card-lookup"] });
      queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });
      console.log("useBlockCard => handleBlockCard result:", result);

      setIsLoading(false);
      setSuccessMsg("Card has been blocked successfully.");
      setShowSuccess(true);
    } catch (error: any) {
      console.log("useBlockCard => handleBlockCard error:", error);
      openError(
        apiErrorMessage(error, "Failed to block card. Please try again."),
      );
    }
  };

  const handleRetry = () => handleBlockCard(lastCardNumber);

  return {
    blockingCard: isLoading,
    blockingCardMsg: loadingMsg,
    blockCardError: showError,
    blockCardErrorMsg: errorMsg,
    blockCardSuccess: showSuccess,
    blockCardSuccessMsg: successMsg,
    handleBlockCard,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  };
}
